import { useHubLang, type HubLang } from '../lib/i18n'
import { COMPOSE } from '../lib/hubCopy'
/* -----------------------------------------------------------------------------
   THE LINE THAT SAYS A LIST IS A PAGE. REC-54.

   `mentionable_people` takes a limit and the database applies it. Sixteen people
   match "david", eight come back, and `.picker__list` caps at 20rem — which
   eight two-line rows fit inside, so there is no scrollbar, CORRECTLY, because
   the browser has everything the server sent. The other eight do not exist as
   far as the screen is concerned. That reads as a broken scroll and is standing
   rule 1 arriving in the UI: the page is *some* of the matches, presented as the
   answer.

   Rule 12 is the one that decides the shape. An empty list is a false claim
   about the world and a refusal is a true claim about the request — a truncated
   list shown as complete is the same false claim, so the honest thing is to say
   what was left out and what to do about it. That turns a dead end into an
   instruction, which is the whole value: nobody types more into a list that
   looks finished.

   ONE HOME, because four surfaces share `mentionable_people` (the @ menu, the
   recognition picker, the shop's audience adder, the stock room manager picker)
   and all four had the identical silent truncation. Standing rule 11: a second
   copy of this sentence is not a duplicate, it is a second answer, and the two
   would disagree the first time either was edited. It is also why the wording
   was settled once (REC-54's `needs`) rather than per screen.

   WHY NOT RAISE THE LIMIT INSTEAD. 942 active people. A two-letter query
   truncates at any limit worth having, and a picker that returns 200 rows is
   worse than one that returns 8. The limit is right; its silence was not.

   THE TOTAL MUST COME FROM THE SERVER and does — `total_matches`, a column on
   the same statement (088), from `count(*) over ()` ahead of the LIMIT. Counting
   the array that arrived would always equal the page size, be perfectly
   self-consistent, and say nothing.
   -------------------------------------------------------------------------- */

/**
 * The sentence, or null when there is nothing to say.
 *
 * NULL WHEN NOTHING WAS TRUNCATED, deliberately. "Showing 6 of 6" is noise on
 * every ordinary search, and a note that is always present is a note nobody
 * reads on the one occasion it matters.
 *
 * The advice branches on whether anything was typed, because "keep typing" is
 * wrong in front of an empty box — the stock room picker opens showing the
 * first 8 of 942 with no query at all, and telling that person to keep typing
 * when they have not started is the screen not knowing what it is looking at.
 *
 * `total` is trusted as the server's, not recomputed: a client that second
 * guesses it is back to counting the page.
 */
export function pageNote(
  shown: number,
  total: number,
  hasQuery: boolean,
  lang: HubLang = 'en',
  /** 336: a list of words ("Show all" on What for) is not a list of people. */
  kind: 'people' | 'words' = 'people',
): string | null {
  if (!Number.isFinite(total) || total <= shown) return null
  // The words are lib/hubCopy.ts's, both languages (SHL-25). The BRANCH stays
  // here, because which sentence is true is this function's decision.
  const t = COMPOSE[lang]
  if (hasQuery) return t.showingMatches(shown, total)
  return kind === 'words' ? t.showingWords(shown, total) : t.showingPeople(shown, total)
}

/**
 * Rendered inside `.picker__panel`, BELOW the list, as `.picker__foot` — which
 * already exists and already carries the hairline that separates it from the
 * rows. It is not a `role="option"`, so it cannot be arrowed onto or chosen;
 * a footnote that can be selected is a row that does nothing.
 *
 * `aria-live` is deliberately absent. The note changes on every keystroke of a
 * debounced search, and a live region would read a new sentence three times a
 * second over the top of somebody's typing. The listbox's own count is what a
 * screen reader announces.
 */
export function PickerFoot({
  shown,
  total,
  hasQuery,
  kind = 'people',
}: {
  shown: number
  total: number
  hasQuery: boolean
  kind?: 'people' | 'words'
}) {
  const note = pageNote(shown, total, hasQuery, useHubLang(), kind)
  if (!note) return null
  return <p className="picker__foot picker__foot--note">{note}</p>
}
